<script>
import axios         from "axios";
import FeedbackUtils from "@/js/feedback-utils";
import Utils         from "@/js/utils";
import RepUtils      from "@/js/reputation-utils";
import DataUtils     from "@/js/data-utils";
import dayjs         from "dayjs";

export default {
  data() {
    return {
      loading: false,
      polarityClass: {
        positive: { icon: "fa-smile text-success", tag: "bg-success"   },
        neutral:  { icon: "fa-meh text-secondary", tag: "bg-secondary" },
        negative: { icon: "fa-frown text-danger",  tag: "bg-danger"    }
      },
      showSubratings: false
    };
  },
  props: ["review", "reviewIndex", "cache", "params", "translations", "comments", "sources", "user", "insideExtension", "additionalInfo", "polaritiesToShow"],
  inject: ["modals", "setSelectedItemId"],
  mounted() {
    this.init();
  },
  methods: {
    ...DataUtils,
    ...FeedbackUtils,
    ...RepUtils,
    ...Utils.extension,
    sourceIcon: Utils.sources.iconUrl,
    async init() {
      if (!this.review?.id) return;
      this.mergeSentiment();
      this.review.notes?.sort((a, b) => dayjs(b.date).diff(dayjs(a.date)));
      this.review.reviewer_name = this.review.reviewer.name;
      this.review.creation_date = dayjs(this.review.published_date).format("DD MMM YYYY");
      this.review.reviewer_country = this.review.reviewer.country.name;
      if (this.review.language) this.review.language_name_translated = this.locales(this.review.language).name;
      if (this.review.travel_composition) this.review.travel_composition_name = this.$t(`travel_composition.${this.review.travel_composition}`);
    },
    async getTranslation() {
      this.translations[this.$i18n.locale] ||= {};
      if (this.translations[this.$i18n.locale][`review_${this.review.id}`]) {
        this.cache[this.params.page][this.reviewIndex].translated = true;
        return;
      }
      this.translations[this.$i18n.locale][`review_${this.review.id}`] = {};
      this.loading = true;
      let payload = {
        data: { ...(this.review.title?.length) && { title: this.review.title } },
        locale: this.$i18n.locale
      }
      this.review.comments?.forEach(comment => {
        if (comment.kind != "manager") payload.data[`comment_${comment.kind}`] = comment.comment;
      });

      let response = await axios.post("/v3/translate", payload);
      if (!this.cache[this.params.page][this.reviewIndex].language) this.cache[this.params.page][this.reviewIndex].language = response.language;
      this.cache[this.params.page][this.reviewIndex].translatedFrom = response.data.language;
      response = response.data.data;

      if (this.review.title?.length) this.translations[this.$i18n.locale][this.review.title] = this.DOMParser(response.title?.[0]);
      this.review.comments?.forEach(comment => {
        if (comment.kind != "manager") {
          this.translations[this.$i18n.locale][`review_${this.review.id}`][`comment_${comment.kind}`] = this.DOMParser(response[`comment_${comment.kind}`]?.[0]);
        }
      });

      this.cache[this.params.page][this.reviewIndex].translated = true;
      this.loading = false;
      this.$forceUpdate();
    },
    mergeSentiment() {
      if (this.review.opinions?.length) {
        let merged = [];
        this.review.opinions.forEach(obj => {
          if (!this.polaritiesToShow.includes(obj.polarity)) return;
          let sentiment = merged.find(m => m.polarity == obj.polarity);
          if (!sentiment) merged.push(obj);
          else sentiment.topics = [...new Set(sentiment.topics.concat(obj.topics))];
        });
        this.review.filteredOpinions = merged;
      }
    },
    formatRating(rating) {
      if (rating) return Utils.formatNumber(Utils.round(rating / 10));
      return null;
    },
    tdClass(value) {
      return Utils.ratings.toCss100(value);
    },
    showTranslateButton() {
      return (this.$i18n.locale != this.review.language) && (this.review.comments.title || this.review.comments?.length);
    },
    useTitle() {
      if (this.cache[this.params.page][this.reviewIndex].translated && this.translations[this.$i18n.locale]?.[this.review.title])
        return this.translations[this.$i18n.locale][this.review.title];
      return this.review.title;
    },
    moreInfoLabel(page, index) {
      if (this.cache[page][index].showMoreInfo) return this.$t("general.collapse");
      return this.$t("general.expand");
    },
    usePolarity(polarity) {
      return this.review.filteredOpinions.find(e => e.polarity == polarity)?.topics;
    },
    by(note) {
      return this.$t("daily_operations.reviews.by_on", { name: note.user, date: dayjs(note.date).format("DD MMM YYYY - hh:mm A") })
    },
    translatedFrom() {
      if (this.cache[this.params.page][this.reviewIndex].language)
        return `${this.$t("surveys_responses.translated_from")} ${this.locales(this.cache[this.params.page][this.reviewIndex].language).name}.`;
      return `${this.$t("surveys_responses.translated_from")} ${this.cache[this.params.page][this.reviewIndex].translatedFrom}.`;
    },
    groupTopics() {
      const result = [];
      this.review.ratings.forEach(rating => {
        const category    = rating.category;
        const find        = result.find(r => r.topic == category);
        const ratingTopic = rating.topic.replace(/_general$/, "");
        let title         = `${this.$t(`topics.${category}`)} - ${this.$t("topics.general")}`;
        if (ratingTopic != category)
          title = `${this.$t(`topics.${category}`)} - ${this.$t(`topics.${ratingTopic}`)}`;
        rating.title = title;
        if (find) {
          find.children.push({ ...rating });
          find.count = find.count + 1;
          find.sum   = find.sum + rating.rating;
        } else {
          result.push({
            topic: category,
            title: this.$t(`ratings.rating_comparison.kpis.${category}`),
            children: [rating],
            count: 1,
            sum: rating.rating
          });
        }
      });
      this.review.topics = result;
    },
    toggleModal(field, value) {
      this.setSelectedItemId(this.review.id);
      const isBool = typeof value === "boolean";
      if (field == "reply") this.modals.reply.review = isBool ? value : !this.modals.reply.review;
      else this.modals[field] = isBool ? value : !this.modals[field];
    },
    openReplyModal() {
      this.setDomainCookie();
      this.toggleModal("reply", true);
    }
  },
  computed: {
    reviewerName() {
      return this.review.reviewer?.name || this.$t("surveys_responses.no_name");
    },
    rating() {
      return this.review.ratings?.find(r => r.category == "overall")?.rating;
    },
    reviewRatings() {
      this.groupTopics();
      let res = (this.review.topics || []).map(t => ({ ...t, rating: t.sum / t.count }));
      if (this.showSubratings) res = (this.review.topics || []).flatMap(t => t.children);

      res = res.sort((a, b) => {
        if (a.title < b.title) return -1;
        if (a.title > b.title) return  1;
        return 0;
      });
      res.forEach((d, i) => { // put overall at the first position
        if (d.topic === "overall" || d.category == "overall") {
          res.splice(i, 1);
          res.unshift(d);
        }
      });
      return res;
    }
  }
}
</script>

<template>
  <li class="event-list review" :class="{ 'cursor-loading': loading, 'px-2 border-0': insideExtension }">
    <div class="timeline-icon rounded-circle" :class="tdClass(rating)" >
      <h4 :class="{ 'no-rating': !rating, 'font-size-16': insideExtension }">
        {{ !rating ? "-" : formatRating(rating) }}
      </h4>
    </div>

    <div class="event-content" :class="{ 'p-3 hide-before': insideExtension, 'p-4': !insideExtension }">
      <div class="response-header d-flex align-items-center justify-content-between mt-0 mb-3 font-18 pb-3">
        <div class="d-flex align-items-center">
          <img v-if="review.source" class="rounded me-0 me-sm-3 ratio-1x1" :src=sourceIcon(review.source) >

          <div class="mt-1 mt-sm-0">
            <h4 class="mb-0">{{ useTitle() }}</h4>
            <span class="mb-0 me-0 me-sm-3">{{ $t("daily_operations.reviews.by_name", { name: reviewerName }) }}</span>
          </div>

          <a v-if="review.reviewer && review.reviewer.email" :href="review.reviewer.email" target="_blank" class="text-primary">
            {{ review.reviewer.email }}
          </a>
        </div>

        <div v-if="review.company && review.company.name" class="float-end">
          {{ $t("daily_operations.reviews.for_company", { name: review.company.name }) }}
        </div>
      </div>

      <div class="review">
        <template v-if="comments">
          <div class="mb-4" v-for="(comment, type) in comments" :key="type + review.id">
            <div v-if="type == 'manager'" class="card-body rounded bg-light">
              <div class="d-flex flex-column flex-sm-row align-items-sm-end mb-3">
                <h4 class="m-0">{{ $t(`daily_operations.reviews.comment_type.${type}`) }}</h4>
                <div v-if="comment.temp" class="mt-1 mt-sm-0 ms-sm-2 d-flex align-items-center text-muted">
                  <i class="mdi mdi-timer-outline me-1"></i>
                  <div>{{ $t("daily_operations.reviews.wait_source", { source: review.source_name }) }}</div>
                </div>
              </div>
              <span v-html="comment.text" class="white-space-pre"></span>
            </div>
            <template v-else>
              <h4 class="mb-1">{{ $t(`daily_operations.reviews.comment_type.${type}`) }}</h4>
              <span v-html="comment.text" class="white-space-pre"></span>
            </template>
          </div>
        </template>
      </div>

      <div class="bottom-navbar" @click.stop>
        <button @click="toggleMoreInfo(params.page, reviewIndex)" class="btn btn-outline-primary me-5 col-12 col-sm-auto mb-2 mb-sm-0" data-bs-toggle="collapse" :data-bs-target="`#review_${review.id}`" aria-expanded="false" :aria-controls="`review_${review.id}`">
          {{ moreInfoLabel(params.page, reviewIndex) }}
        </button>

        <button v-if="showTranslateButton(cache[params.page][reviewIndex])" type="button" class="btn btn-outline-primary me-4 col-12 col-sm-auto mb-2 mb-sm-0"
          @click="cache[params.page][reviewIndex].translated ? revertTranslation(reviewIndex) : getTranslation(review, reviewIndex)" >
          {{ cache[params.page][reviewIndex].translated
              ? $t("surveys_responses.revert")
              : $t("surveys_responses.translate") }}
        </button>

        <a :href="forward(review)" class="btn btn-outline-primary me-4 col-12 col-sm-auto mb-2 mb-sm-0">
          {{ $t("daily_operations.reviews.forward") }}
        </a>

        <button :disabled="false" class="btn btn-outline-primary me-4 col-12 col-sm-auto mb-2 mb-sm-0" @click="toggleModal('note')">
          {{ $t("daily_operations.reviews.add_note") }}
        </button>

        <button v-if="review.respondable" class="btn btn-outline-primary me-4 col-12 col-sm-auto mb-2 mb-sm-0" @click="openReplyModal" :disabled="loading">
          {{ $t("daily_operations.reviews.reply") }}
        </button>

        <div v-else-if="!review.respondable" class="btn me-4 col-12 col-sm-auto mb-2 mb-sm-0 p-0" v-b-tooltip.hover :title="$t('daily_operations.reviews.reply_disabled_tooltip')">
          <a href="#" class="btn btn-outline-primary disabled w-100">{{ $t("daily_operations.reviews.reply") }}</a>
        </div>
      </div>

      <div class="translated-from mt-2" v-if="cache[params.page][reviewIndex].translated" style="font-size: 13px;" >
        {{ translatedFrom() }}
      </div>

      <!-- more info box -->
      <div class="collapse row mt-5 pb-3 pt-2" :id="`review_${review.id}`">
        <!-- details -->
        <div class="table-responsive mb-0 col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6">
          <h5 class="m-0">{{ $t("daily_operations.reviews.details") }}</h5>
          <table class="w-100">
            <template v-for="(row, index) in additionalInfo">
              <tr v-if="review[row.field]" :key="index">
                <td class="py-2">
                  <label class="me-2 mb-0">{{ $t(`surveys_responses.additional_info_fields.${row.i18nPath}`) }}</label>
                </td>
                <td class="justify-content-start">
                  {{ review[row.field] }}
                </td>
              </tr>
            </template>
          </table>
        </div>

        <!-- ratings -->
        <div v-if="reviewRatings.length" class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mt-5 mt-lg-0">
          <div class="d-flex align-items-center justify-content-between">
            <h5 class="m-0">{{ $t("daily_operations.reviews.ratings") }}</h5>
            <b-form-checkbox id="showSubratings" v-model="showSubratings" @click.stop switch class="mb-3 pt-2" size="lg">
              <label for="showSubratings" class="font-size-14">{{ $t("daily_operations.analytics.show_sub_ratings") }}</label>
            </b-form-checkbox>
          </div>

          <div v-for="(topic, i) in reviewRatings" :key="`${topic.topic}_${i}`" class="mt-3">
            <div class="d-flex align-items-center justify-content-between">
              <span class="mb-2 d-flex align-items-center">
                <h5 class="mb-0 me-2">{{ topic.title }}</h5>
                <span v-if="showSubratings && topic.source_title" v-b-tooltip.hover :title="`${$t('daily_operations.reviews.original_source')}: ${topic.source_title}`">
                  <i class="mdi mdi-information-outline"></i>
                </span>
              </span>
              <h5>{{ formatRating(topic.rating) }}</h5>
            </div>
            <div class="progress">
              <div class="progress-bar bg-replab" :class="tdClass(topic.rating)" :style="`width: ${topic.rating}%;`"></div>
            </div>
          </div>
        </div>

        <!-- sentiments -->
        <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mt-1 mt-lg-0">
          <template v-if="review.filteredOpinions && review.filteredOpinions.length > 0">
            <h5 class="question-title mb-3"> {{ $t("surveys_responses.sentiments") }} </h5>
            <div class="d-flex flex-column justify-content-start">
              <template v-for="p in polaritiesToShow" :key="p">
                <div class="me-4 mb-2" :class="p" v-if="usePolarity(p)">
                  <div class="d-flex">
                    <i class="fa font-size-22 me-2" :class="polarityClass[p].icon" v-b-tooltip.hover :title="$t(`surveys_responses.${p}_sentiment`)"></i>
                    <div class="d-flex flex-wrap">
                      <div class="tag text-center" :class="polarityClass[p].tag" v-for="(property, index) in usePolarity(p)" :key="property + index">
                        {{ $t(`topics.${property}`) }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- previous saved notes -->
        <template v-if="review.notes && review.notes.length">
          <h5 class="mt-5 mb-1">{{ $t("daily_operations.reviews.notes") }}</h5>
          <div v-for="note in review.notes" :key="note.date" class="d-flex flex-column mb-3">
            <span>{{ note.body }}</span>
            <span class="text-muted">{{ by(note) }}</span>
          </div>
        </template>
      </div>
    </div>

    <div class="event-date">
      <h5 class="mt-0 mb-0">{{ getMonth(review.published_date) }}&nbsp;</h5>
      <h5 class="mt-0 mb-0">{{ toDate(review.published_date).getDate() }}&nbsp;</h5>
      <p class="mb-0">
        <strong>{{ toDate(review.published_date).getFullYear() }}&nbsp;</strong>
      </p>
    </div>
  </li>
</template>
